import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import {
	type EditorState,
	NodeSelection,
	PluginKey,
	TextSelection,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { CellSelection } from '@atlaskit/editor-tables';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';
import { isEditorRemixLifecycleWriteAuthorityHeld } from '../../ui/utils/editor-remix-callback-registry';

import {
	createRemixThinkingWidgetDecoration,
	getRemixThinkingWidgetContainer,
	renderRemixThinkingPortal,
	THINKING_WIDGET_KEY,
} from './remix-thinking-decoration';

const THINKING_HIGHLIGHT_CLASS = 'remix-thinking-highlight';

export type SavedSelection = {
	anchorCellPos?: number;
	from: number;
	headCellPos?: number;
	isNodeSelection: boolean;
	to: number;
};

// types in this interface use | undefined to make sure we don't miss the field when updating the plugin state
export type RemixThinkingPluginState = {
	contentType?: string;
	decorationSet: DecorationSet;
	dynamicUiSubtypeForUpdate: string | undefined;
	historyEntryForUpdate: RemixHistoryEntryForUpdate | undefined;
	isComplete: boolean | undefined;
	isThinking: boolean;
	isUpdate: boolean | undefined;
	remixAttemptId: string | undefined;
	savedSelection?: SavedSelection;
};

export type RemixThinkingPresentationUpdate =
	| {
			anchorCellPos?: number;
			cellSelectionRanges?: Array<{ from: number; to: number }>;
			contentType?: string;
			dynamicUiSubtypeForUpdate?: string;
			headCellPos?: number;
			historyEntryForUpdate?: RemixHistoryEntryForUpdate;
			isNodeSelection?: boolean;
			isUpdate?: boolean;
			kind: 'start';
			remixAttemptId?: string;
			suppressThinkingHighlight?: boolean;
			thinkingSelectionFrom: number;
			thinkingSelectionTo: number;
	  }
	| { kind: 'stop'; preserveSavedSelection?: boolean }
	| { kind: 'clear-decoration' }
	| { kind: 'reset' };

// `legacy-*` kinds are the control writer, dropped in `reducePluginState` while a machine session
// owns (or has terminally latched) the editor. `machine-projection` is the lifecycle machine's
// write: never suppressed.
export type RemixThinkingLifecycleUpdate =
	| { kind: 'legacy-start' }
	| { kind: 'legacy-complete' }
	| { kind: 'legacy-complete-false' }
	| { kind: 'legacy-stop' }
	| { kind: 'legacy-clear' }
	| { kind: 'legacy-reset' }
	| { isComplete: boolean; isThinking: boolean; kind: 'machine-projection' };

// `presentation` owns decorations, saved selection and update metadata; `lifecycle` owns
// `isThinking` / `isComplete`. Presentation is applied first.
export type RemixThinkingPluginMeta = {
	lifecycle?: RemixThinkingLifecycleUpdate;
	presentation?: RemixThinkingPresentationUpdate;
};

export const remixThinkingPluginKey: PluginKey<RemixThinkingPluginState> =
	new PluginKey<RemixThinkingPluginState>('remixThinkingPlugin');

function getInitialState(): RemixThinkingPluginState {
	return {
		isThinking: false,
		isComplete: false,
		isUpdate: false,
		historyEntryForUpdate: undefined,
		dynamicUiSubtypeForUpdate: undefined,
		remixAttemptId: undefined,
		decorationSet: DecorationSet.empty,
	};
}

/**
 * Whether every field the `reset` presentation kind clears is already cleared. `isThinking` and
 * `isComplete` are excluded: they belong to the lifecycle channel, which `reset` leaves alone.
 */
function hasNoPresentationState(pluginState: RemixThinkingPluginState): boolean {
	return (
		pluginState.contentType === undefined &&
		pluginState.remixAttemptId === undefined &&
		pluginState.isUpdate === false &&
		pluginState.historyEntryForUpdate === undefined &&
		pluginState.dynamicUiSubtypeForUpdate === undefined &&
		pluginState.savedSelection === undefined &&
		pluginState.decorationSet.find().length === 0
	);
}

function reducePresentationUpdate({
	api,
	getIntl,
	isViewMode,
	nodeViewPortalProviderAPI,
	newState,
	presentation,
	pluginState,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	getIntl: PMPluginFactoryParams['getIntl'];
	isViewMode: boolean;
	newState: EditorState;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	pluginState: RemixThinkingPluginState;
	presentation: RemixThinkingPresentationUpdate;
}): RemixThinkingPluginState {
	switch (presentation.kind) {
		case 'reset': {
			// Same reference when there is nothing to clear, so a caller that pairs this with a
			// lifecycle write does not churn the plugin state on a no-op.
			if (hasNoPresentationState(pluginState)) {
				return pluginState;
			}
			return {
				...getInitialState(),
				isComplete: pluginState.isComplete,
				isThinking: pluginState.isThinking,
			};
		}

		case 'start': {
			const from = presentation.thinkingSelectionFrom;
			const to = presentation.thinkingSelectionTo;
			let decorations: Decoration[];

			if (presentation.suppressThinkingHighlight) {
				decorations = [];
			} else if (presentation.cellSelectionRanges) {
				decorations = presentation.cellSelectionRanges.map((range) =>
					Decoration.inline(range.from, range.to, { class: THINKING_HIGHLIGHT_CLASS }),
				);
			} else if (presentation.isNodeSelection) {
				decorations = [Decoration.node(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
			} else {
				decorations = [Decoration.inline(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
			}

			if (!isViewMode && fg('platform_editor_ai_remix_in_editor_loading')) {
				decorations.push(
					createRemixThinkingWidgetDecoration({
						api,
						contentType: presentation.contentType,
						doc: newState.doc,
						getIntl,
						isComplete: false,
						isPlaceholder: fg('platform_editor_ai_remix_toolbar')
							? (presentation.isUpdate ?? false)
							: false,
						remixAttemptId: presentation.remixAttemptId,
						nodeViewPortalProviderAPI,
						to,
					}),
				);
			}

			const decorationSet = DecorationSet.create(newState.doc, decorations);
			const result: RemixThinkingPluginState = {
				contentType: fg('platform_editor_ai_remix_in_editor_loading')
					? presentation.contentType
					: undefined,
				remixAttemptId: presentation.remixAttemptId,
				isThinking: pluginState.isThinking,
				isUpdate: fg('platform_editor_ai_remix_toolbar') ? presentation.isUpdate : undefined,
				dynamicUiSubtypeForUpdate: fg('platform_editor_ai_remix_toolbar')
					? presentation.dynamicUiSubtypeForUpdate
					: undefined,
				historyEntryForUpdate: fg('platform_editor_ai_remix_toolbar')
					? presentation.historyEntryForUpdate
					: undefined,
				isComplete: pluginState.isComplete,
				decorationSet,
				savedSelection: {
					from,
					to,
					isNodeSelection: presentation.isNodeSelection ?? false,
					anchorCellPos: presentation.anchorCellPos,
					headCellPos: presentation.headCellPos,
				},
			};
			return result;
		}

		case 'clear-decoration': {
			if (!fg('platform_editor_ai_remix_in_editor_loading') || !pluginState.isThinking) {
				return pluginState;
			}

			const result: RemixThinkingPluginState = {
				contentType: pluginState.contentType,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: pluginState.isThinking,
				isUpdate: pluginState.isUpdate,
				isComplete: pluginState.isComplete,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				decorationSet: DecorationSet.empty,
				savedSelection: pluginState.savedSelection,
			};
			return result;
		}

		case 'stop': {
			const decorations: Decoration[] = [];
			if (!isViewMode && pluginState.isComplete && pluginState.savedSelection?.to) {
				decorations.push(
					createRemixThinkingWidgetDecoration({
						api,
						// The pre-split stop meta never carried a contentType.
						contentType: undefined,
						doc: newState.doc,
						getIntl,
						isComplete: false,
						isPlaceholder: true,
						remixAttemptId: pluginState.remixAttemptId,
						nodeViewPortalProviderAPI,
						to: pluginState.savedSelection.to,
					}),
				);
			}
			const result: RemixThinkingPluginState = {
				contentType: fg('platform_editor_ai_remix_in_editor_loading')
					? pluginState.contentType
					: undefined,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: pluginState.isThinking,
				isUpdate: pluginState.isUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				isComplete: pluginState.isComplete,
				decorationSet: fg('platform_editor_ai_remix_in_editor_loading')
					? DecorationSet.create(newState.doc, decorations)
					: DecorationSet.empty,
				savedSelection: presentation.preserveSavedSelection
					? pluginState.savedSelection
					: undefined,
			};
			return result;
		}
	}
}

// Returns `undefined` where the pre-split reducer fell through without a result.
function reduceLifecycleUpdate({
	lifecycle,
	pluginState,
}: {
	lifecycle: RemixThinkingLifecycleUpdate;
	pluginState: RemixThinkingPluginState;
}): RemixThinkingPluginState | undefined {
	switch (lifecycle.kind) {
		case 'legacy-start': {
			const result: RemixThinkingPluginState = {
				contentType: pluginState.contentType,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: true,
				isUpdate: pluginState.isUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				isComplete: fg('platform_editor_ai_remix_in_editor_loading') ? false : undefined,
				decorationSet: pluginState.decorationSet,
				savedSelection: pluginState.savedSelection,
			};
			return result;
		}

		case 'legacy-complete': {
			if (!fg('platform_editor_ai_remix_in_editor_loading')) {
				return undefined;
			}
			if (pluginState.isComplete || !pluginState.isThinking) {
				return pluginState;
			}

			const result: RemixThinkingPluginState = {
				contentType: pluginState.contentType,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: pluginState.isThinking,
				isUpdate: pluginState.isUpdate,
				isComplete: true,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				decorationSet: pluginState.decorationSet,
				savedSelection: pluginState.savedSelection,
			};
			return result;
		}

		case 'legacy-complete-false': {
			if (!fg('platform_editor_ai_remix_in_editor_loading')) {
				return undefined;
			}
			if (pluginState.isComplete === false) {
				return pluginState;
			}

			const result: RemixThinkingPluginState = {
				contentType: undefined,
				remixAttemptId: undefined,
				isThinking: false,
				isComplete: false,
				isUpdate: false,
				historyEntryForUpdate: undefined,
				dynamicUiSubtypeForUpdate: undefined,
				decorationSet: DecorationSet.empty,
				savedSelection: undefined,
			};
			return result;
		}

		case 'legacy-stop': {
			const result: RemixThinkingPluginState = {
				contentType: pluginState.contentType,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: false,
				isUpdate: pluginState.isUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				isComplete: fg('platform_editor_ai_remix_in_editor_loading')
					? pluginState.isComplete
					: undefined,
				decorationSet: pluginState.decorationSet,
				savedSelection: pluginState.savedSelection,
			};
			return result;
		}

		case 'legacy-clear': {
			if (!fg('platform_editor_ai_remix_in_editor_loading')) {
				return undefined;
			}
			if (!pluginState.isThinking) {
				return pluginState;
			}

			const result: RemixThinkingPluginState = {
				contentType: pluginState.contentType,
				remixAttemptId: pluginState.remixAttemptId,
				isThinking: true,
				isUpdate: pluginState.isUpdate,
				isComplete: false,
				historyEntryForUpdate: pluginState.historyEntryForUpdate,
				dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
				decorationSet: pluginState.decorationSet,
				savedSelection: pluginState.savedSelection,
			};
			return result;
		}

		case 'legacy-reset': {
			return getInitialState();
		}

		case 'machine-projection': {
			const { isComplete, isThinking } = lifecycle;
			if (isThinking === pluginState.isThinking && isComplete === pluginState.isComplete) {
				return pluginState;
			}
			return { ...pluginState, isThinking, isComplete };
		}
	}
}

/**
 * normal flow:
 * - reset when show modal
 * - start: isThinking: true
 * - complete: isThinking: true; isComplete: true (after we have final network response)
 * - stop: isThinking: false; isComplete: true; (after onCompleteAnimationFinished)
 * 	 - we can't reset isComplete when stop thinking because we need it to show the ready toast
 *   - if the ready toast is not shown, currently, we're not resetting isComplete at all
 *   - if this become a bigger issue later, we need to properly reset it
 * - optional: complete-false: isThinking: false; isComplete: false; (after clicking ready toast)
 *
 * update flow:
 * - start: isThinking: true; isUpdate: true;
 * - rest is same as normal flow
 */
function reducePluginState({
	api,
	getIntl,
	isViewMode,
	nodeViewPortalProviderAPI,
	newState,
	meta,
	pluginState,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	getIntl: PMPluginFactoryParams['getIntl'];
	isViewMode: boolean;
	meta: RemixThinkingPluginMeta;
	newState: EditorState;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	pluginState: RemixThinkingPluginState;
}): RemixThinkingPluginState | undefined {
	if (!meta.presentation && !meta.lifecycle) {
		return undefined;
	}

	let result = pluginState;

	if (meta.presentation) {
		result = reducePresentationUpdate({
			api,
			getIntl,
			isViewMode,
			nodeViewPortalProviderAPI,
			newState,
			presentation: meta.presentation,
			pluginState: result,
		});
	}

	if (meta.lifecycle) {
		const isLegacyLifecycle = meta.lifecycle.kind.startsWith('legacy-');
		if (isLegacyLifecycle && isEditorRemixLifecycleWriteAuthorityHeld(api)) {
			return meta.presentation ? result : undefined;
		}
		return reduceLifecycleUpdate({ lifecycle: meta.lifecycle, pluginState: result });
	}

	return result;
}

export function createRemixThinkingPmPlugin({
	api,
	config,
	dispatch,
	getIntl,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin {
	return new SafePlugin({
		key: remixThinkingPluginKey,
		state: {
			init(): RemixThinkingPluginState {
				return getInitialState();
			},
			apply(tr, pluginState, _oldState, newState): RemixThinkingPluginState {
				const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
				const meta: RemixThinkingPluginMeta | undefined = tr.getMeta(remixThinkingPluginKey);

				if (meta) {
					const result = reducePluginState({
						api,
						getIntl,
						isViewMode,
						nodeViewPortalProviderAPI,
						newState,
						meta,
						pluginState,
					});
					if (result) {
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				if (pluginState.isThinking && pluginState.decorationSet !== DecorationSet.empty) {
					let mappedDecorationSet = pluginState.decorationSet.map(tr.mapping, newState.doc);

					// re-create the thinking widget decoration if the widget is not in the mapped decoration set while thinking
					// it can be removed when updating the remix, which will replace the node and delete the decoration
					if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
						const thinkingWidgetDecoration = mappedDecorationSet.find(
							undefined,
							undefined,
							(spec) => {
								return spec?.key === THINKING_WIDGET_KEY;
							},
						);
						if (!thinkingWidgetDecoration.length && pluginState.savedSelection?.to) {
							mappedDecorationSet = mappedDecorationSet.add(newState.doc, [
								createRemixThinkingWidgetDecoration({
									api,
									contentType: pluginState.contentType,
									doc: newState.doc,
									getIntl,
									isComplete: false,
									isPlaceholder: fg('platform_editor_ai_remix_toolbar')
										? (pluginState.isUpdate ?? false)
										: false,
									remixAttemptId: pluginState.remixAttemptId,
									nodeViewPortalProviderAPI,
									to: tr.mapping.map(pluginState.savedSelection.to),
								}),
							]);
						}
					}

					if (mappedDecorationSet !== pluginState.decorationSet) {
						const mappedSavedSelection = pluginState.savedSelection
							? {
									...pluginState.savedSelection,
									from: tr.mapping.map(pluginState.savedSelection.from),
									to: tr.mapping.map(pluginState.savedSelection.to),
									anchorCellPos:
										pluginState.savedSelection.anchorCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.anchorCellPos)
											: undefined,
									headCellPos:
										pluginState.savedSelection.headCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.headCellPos)
											: undefined,
								}
							: undefined;
						const result: RemixThinkingPluginState = {
							...pluginState,
							decorationSet: mappedDecorationSet,
							savedSelection: mappedSavedSelection,
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				return pluginState;
			},
		},

		view() {
			return {
				update(view, previousState) {
					const previousPluginState = remixThinkingPluginKey.getState(previousState);
					const currentPluginState = remixThinkingPluginKey.getState(view.state);

					const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
					// Scroll thinking bar into view when thinking starts (for large blocks like tables)
					if (
						!isViewMode &&
						currentPluginState?.isThinking &&
						!previousPluginState?.isThinking &&
						fg('platform_editor_ai_remix_in_editor_loading')
					) {
						requestAnimationFrame(() => {
							const container = getRemixThinkingWidgetContainer(view);
							container?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
						});
					}

					if (
						!isViewMode &&
						currentPluginState?.isThinking &&
						!previousPluginState?.isComplete &&
						currentPluginState.isComplete &&
						(fg('platform_editor_ai_remix_toolbar') ? !currentPluginState.isUpdate : true) &&
						fg('platform_editor_ai_remix_in_editor_loading')
					) {
						const container = getRemixThinkingWidgetContainer(view);
						if (!container) {
							return;
						}

						renderRemixThinkingPortal({
							api,
							container,
							contentType: currentPluginState.contentType,
							getIntl,
							isComplete: currentPluginState.isComplete ?? false,
							remixAttemptId: currentPluginState.remixAttemptId,
							nodeViewPortalProviderAPI,
						});
					}
				},
			};
		},

		appendTransaction(_transactions, oldState, newState) {
			const oldPluginState = remixThinkingPluginKey.getState(oldState);
			const newPluginState = remixThinkingPluginKey.getState(newState);

			// only restore selection if the plugin state is not complete
			// which means the remix is cancelled
			// we don't want to restore selection if the remix is complete
			// which will trigger floating toolbar
			if (
				oldPluginState?.isThinking &&
				!newPluginState?.isThinking &&
				newPluginState?.savedSelection &&
				!(newPluginState?.isComplete && fg('platform_editor_ai_remix_in_editor_loading'))
			) {
				const { savedSelection } = newPluginState;
				const tr = newState.tr;

				if (
					savedSelection.anchorCellPos !== undefined &&
					savedSelection.headCellPos !== undefined
				) {
					tr.setSelection(
						CellSelection.create(tr.doc, savedSelection.anchorCellPos, savedSelection.headCellPos),
					);
				} else if (savedSelection.isNodeSelection) {
					tr.setSelection(NodeSelection.create(tr.doc, savedSelection.from));
				} else {
					tr.setSelection(TextSelection.create(tr.doc, savedSelection.from, savedSelection.to));
				}

				return tr;
			}

			return null;
		},
		props: {
			decorations(state) {
				return remixThinkingPluginKey.getState(state)?.decorationSet ?? DecorationSet.empty;
			},
		},
	});
}
